// src/lib/apiConfig.js — v200.53 (2026-08-14)
//
// المبدأ الحاكم:
//   القاعدة التاسعة من الدستور تحكم زمن الخادم. مهلة المتصفّح
//   شيء آخر ويجب أن تكون أطول دائماً من سقف الخادم لنفس العملية.
//   مهلة واجهة أقصر من سقف خادمها تُنتج فشلاً ظاهراً لعمل نجح —
//   وهو العيب نفسه الذي بُنيت هذه الطبقة لإزالته.
//
// هذا الملف هو الموضع الوحيد في المستودع الذي يجوز أن يظهر فيه
// رقم مهلة حرفي. الحارس G3 (`test_network_layer_singleton.py`)
// يمسك أي تسرّب.
//
// الأصناف الأربعة (v200.53 · معتمدة من المالك):
//
//   AUTH        = 10000    نداء توثيق قصير — لا سقف خادم في القاعدة #9.
//   QUICK_READ  = 20000    الطبقة A سقفها 5s (القاعدة #9) — ×4 هامش.
//   AI_CALL     = 300000   نداء نموذج اللغة سقفه 180s (القاعدة #9) — أطول منه.
//   REPORT      = 270000   التقرير كاملاً سقفه 240s (القاعدة #9) — أطول منه.
//
// كل صنف يُقرأ من متغيّر بيئة خاص به. القيم أعلاه هي التصريح
// الافتراضي الوحيد. عند غياب المتغيّر، يُطبع تحذير واحد على
// console عند التحميل الأول — لا صمت (الفشل الصامت ممنوع).

const _FALLBACK = {
  // AUTH: نداء توثيق قصير. لا سقف خادم مذكور في القاعدة #9.
  //       نختار 10s كسقف واجهة معقول لتجربة تسجيل الدخول.
  AUTH:       10000,
  // QUICK_READ: قراءات اللوحات والشاشات (الطبقة A في القاعدة #9،
  //             سقفها 5s على الخادم). المهلة الأمامية = 4× السقف
  //             لضمان أن يعود كل نداء صحّي قبل الإلغاء.
  QUICK_READ: 20000,
  // AI_CALL: نداء نموذج اللغة (بند القاعدة #9: «مهلة نداء نموذج
  //          اللغة 180 ثانية»). المهلة الأمامية أطول: 300s.
  AI_CALL:    300000,
  // REPORT: التقرير كاملاً (بند القاعدة #9: «التقرير كاملاً 240
  //         ثانية قبل الفشل الصريح»). المهلة الأمامية أطول: 270s.
  REPORT:     270000,
};

const _ENV_KEY = {
  AUTH:       "REACT_APP_API_TIMEOUT_AUTH_MS",
  QUICK_READ: "REACT_APP_API_TIMEOUT_QUICK_READ_MS",
  AI_CALL:    "REACT_APP_API_TIMEOUT_AI_CALL_MS",
  REPORT:     "REACT_APP_API_TIMEOUT_REPORT_MS",
};

function _resolve(cls) {
  const key = _ENV_KEY[cls];
  const raw = (typeof process !== "undefined" && process.env)
    ? process.env[key]
    : undefined;
  if (raw === undefined || raw === null || raw === "") {
    console.warn(
      `[apiConfig] ${key} غير مُعرَّف — يُستخدم الافتراضي ` +
      `${_FALLBACK[cls]}ms للصنف ${cls}. عرِّف المتغيّر في .env لضبطه.`
    );
    return _FALLBACK[cls];
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    console.warn(
      `[apiConfig] ${key}=${JSON.stringify(raw)} غير صالح — يُستخدم ` +
      `الافتراضي ${_FALLBACK[cls]}ms للصنف ${cls}.`
    );
    return _FALLBACK[cls];
  }
  return n;
}

// الأصناف الأربعة المسمّاة — يستوردها المستدعون بالاسم فقط.
export const TIMEOUTS = Object.freeze({
  AUTH:       _resolve("AUTH"),
  QUICK_READ: _resolve("QUICK_READ"),
  AI_CALL:    _resolve("AI_CALL"),
  REPORT:     _resolve("REPORT"),
});

// أسماء الأصناف كقائمة صريحة — يستخدمها الحارس G5 (استدعاء بلا
// صنف من هذه القائمة = فشل بناء).
export const TIMEOUT_CLASSES = Object.freeze(
  Object.keys(TIMEOUTS)  // ["AUTH", "QUICK_READ", "AI_CALL", "REPORT"]
);

// v200.53 — الاستدعاء الافتراضي للنسخة الواحدة (بلا تمرير صنف)
// يستخدم QUICK_READ (الطبقة A في القاعدة #9). هذا هو مصدر
// الحقن في axios.create() داخل lib/api.js.
export const DEFAULT_TIMEOUT_MS = TIMEOUTS.QUICK_READ;


// ═══════════════════════════════════════════════════════════════════
// v200.54 — قدرة مصدر التوثيق (Token Source)
// ═══════════════════════════════════════════════════════════════════
// طبقة الشبكة كانت تفترض جلسة واحدة (`tw_token`). لكن BranchScreen
// يستخدم رمزاً منفصلاً (`tw_branch_screen_token`) لأنّ جلسة الفرع
// PIN‑based وقصيرة، لا تدخل في التدفّق العادي. طبقة تعرف مصدراً
// واحداً فقط تفرض على الملفات المتخصّصة أن تبني نسخ axios خاصة —
// وهذا هو العيب الصنفي.
//
// كل نداء يستطيع أن يُصرّح مصدر رمزه في `config.tokenSource`. المصدر
// يُقرأ من قائمة مسمّاة (لا سلسلة نصّية حرّة). مصدر غير معروف →
// خطأ فوراً في request interceptor. الحارس G6 يمنع أي مصدر خارج
// القائمة على مستوى البناء الثابت.
//
// لا يوجد تغيير في منطق التوثيق أو في أي رمز أو مدّة جلسة. الطبقة
// تنقل «من أين يُقرأ الرمز» فقط، لا «ما هو».
export const TOKEN_SOURCES = Object.freeze({
  // الجلسة الرئيسية للمستخدم — القراءة الافتراضية من
  // sessionStorage["tw_token"] ثم localStorage["tw_token"].
  MAIN:   { storageKey: "tw_token", label: "الجلسة الرئيسية" },
  // جلسة شاشة الفرع — رمز PIN مستقل، عمره أقصر، مفتاح مخزن مختلف.
  BRANCH: { storageKey: "tw_branch_screen_token", label: "شاشة الفرع" },
  // نداءات علنية (استبيانات، بوابات) — لا رمز مُرسَل.
  NONE:   { storageKey: null, label: "بلا توثيق" },
});

// أسماء المصادر كقائمة صريحة — يستهلكها الحارس G6.
export const TOKEN_SOURCE_NAMES = Object.freeze(
  Object.keys(TOKEN_SOURCES)  // ["MAIN", "BRANCH", "NONE"]
);
